import {useCallback, useEffect, useRef} from 'react';
import {getTabSessionId} from '@/app/helpers/getTabSessionId';
import {sendStats} from '@/common/helpers/stats/sendStats';
import {generateRandomSafeBase64} from '@/common/helpers/utils/generateRandomBase64';

export function useDfpAdBannerAnalytics(params: {
	showAllowed: boolean;
	unitPath: string;
	isInitialRender: boolean;
	interfaceName: string;
	analyticsLabel: string;
}) {
	const {showAllowed, unitPath, isInitialRender, interfaceName, analyticsLabel} = params;
	const clearUnitPath = unitPath.replace(/\//g, '$');
	const showIndexRef = useRef(0);
	const renderedEmptyRef = useRef<boolean | null>(null);
	const loadingIdRef = useRef<string>('');
	const displayEventSentRef = useRef(false);
	const sentStatsRef = useRef<Set<string>>(new Set());
	const sentStatsLoadingIdRef = useRef<string>('');
	loadingIdRef.current ||= generateRandomSafeBase64(16);

	const sendStatsOnce = useCallback((eventName: string, statPath: string) => {
		if (sentStatsLoadingIdRef.current !== loadingIdRef.current) {
			sentStatsLoadingIdRef.current = loadingIdRef.current;
			sentStatsRef.current.clear();
		}

		const dedupeKey = `${loadingIdRef.current}:${eventName}`;
		if (sentStatsRef.current.has(dedupeKey)) {
			return;
		}

		sentStatsRef.current.add(dedupeKey);
		sendStats(statPath);
	}, []);

	const sendDisplayStat = useCallback(
		(eventType: 'display' | 'refresh') => {
			if (eventType === 'refresh') {
				showIndexRef.current += 1;
				loadingIdRef.current = generateRandomSafeBase64(16);
				sentStatsLoadingIdRef.current = loadingIdRef.current;
				sentStatsRef.current.clear();
				displayEventSentRef.current = false;
				renderedEmptyRef.current = null;
			}

			let impressionIndex: number;

			if (eventType === 'display') {
				impressionIndex = showIndexRef.current === 0 && isInitialRender ? 0 : 1;
			} else {
				impressionIndex = showIndexRef.current + 1;
			}

			displayEventSentRef.current = true;
			sendStatsOnce(
				'display',
				`comm/v1/${Date.now()}/display/${loadingIdRef.current}/${getTabSessionId()}/${interfaceName}/${impressionIndex}/am/${analyticsLabel}/${clearUnitPath}`,
			);
		},
		[analyticsLabel, clearUnitPath, interfaceName, isInitialRender, sendStatsOnce],
	);

	// Send analytics
	useEffect(() => {
		if (!showAllowed) {
			return;
		}

		const handleSlotRequested = (event: googletag.events.SlotRequestedEvent) => {
			if (unitPath === event.slot.getAdUnitPath() && displayEventSentRef.current) {
				sendStatsOnce('request', `comm/v1/${Date.now()}/request/${loadingIdRef.current}`);
			}
		};

		const handleSlotResponseReceived = (event: googletag.events.SlotResponseReceived) => {
			if (unitPath === event.slot.getAdUnitPath() && displayEventSentRef.current) {
				const provider = 'adx';
				sendStatsOnce('response', `comm/v1/${Date.now()}/response/${loadingIdRef.current}/${provider}`);
			}
		};

		const handleSlotRendered = (event: googletag.events.SlotRenderEndedEvent) => {
			if (unitPath === event.slot.getAdUnitPath() && displayEventSentRef.current) {
				renderedEmptyRef.current = event.isEmpty;
				sendStatsOnce('render', `comm/v1/${Date.now()}/render/${loadingIdRef.current}`);
			}
		};

		const handleSlotLoad = (event: googletag.events.SlotOnloadEvent) => {
			if (unitPath === event.slot.getAdUnitPath() && displayEventSentRef.current) {
				const status = renderedEmptyRef.current ? 'empty' : 'filled';
				sendStatsOnce('shown', `comm/v1/${Date.now()}/shown/${loadingIdRef.current}/${status}`);
			}
		};

		const handleImpressionViewable = (event: googletag.events.ImpressionViewableEvent) => {
			if (unitPath === event.slot.getAdUnitPath() && displayEventSentRef.current) {
				sendStatsOnce('viewed', `comm/v1/${Date.now()}/viewed/${loadingIdRef.current}`);
			}
		};

		const pubAds = googletag.pubads();
		pubAds.addEventListener('slotRequested', handleSlotRequested);
		pubAds.addEventListener('slotResponseReceived', handleSlotResponseReceived);
		pubAds.addEventListener('slotRenderEnded', handleSlotRendered);
		pubAds.addEventListener('slotOnload', handleSlotLoad);
		pubAds.addEventListener('impressionViewable', handleImpressionViewable);

		return () => {
			pubAds.removeEventListener('slotRequested', handleSlotRequested);
			pubAds.removeEventListener('slotResponseReceived', handleSlotResponseReceived);
			pubAds.removeEventListener('slotRenderEnded', handleSlotRendered);
			pubAds.removeEventListener('slotOnload', handleSlotLoad);
			pubAds.removeEventListener('impressionViewable', handleImpressionViewable);
		};
	}, [showAllowed, unitPath, sendDisplayStat]);

	return sendDisplayStat;
}

useDfpAdBannerAnalytics.displayName = 'useDfpAdBannerAnalytics';
